import { api } from '@/lib/api-client'
import type { 
  LoginRequest, 
  LoginResponse, 
  UserProfile,
  User,
  Tenant,
  Device,
  AppRelease,
  DynamicForm,
  WorkflowDefinition,
  WorkflowDefinitionDetail,
  WorkflowState,
  WorkflowTransition,
  WorkflowInstance,
  WorkflowInstanceDetail,
  WorkflowTask,
  PaginatedResponse,
  HomeCard,
  HomeCardCreate,
  LoginHistoryEntry,
  Submission,
  SubmissionDetail,
  Client,
  ClientCreate,
  ClientUpdate,
  TenantAccess,
} from '@/types'

// Auth API
export const authApi = {
  login: (data: LoginRequest) => 
    api.post<LoginResponse>('/api/auth/login', data),
  myTenants: () =>
    api.get<{ tenants: { slug: string; name: string }[] }>('/api/auth/my-tenants'),
  userTenants: (data: { email: string; password: string }) =>
    api.post<{ tenants: { slug: string; name: string }[] }>('/api/auth/user-tenants', data),
  switchTenant: (tenant_slug: string) =>
    api.post<LoginResponse>('/api/auth/switch-tenant', { tenant_slug }),
  
  logout: () => 
    api.post('/api/auth/logout'),
  
  getProfile: () => 
    api.get<UserProfile>('/api/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) => 
    api.post('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  updateProfile: (data: { first_name?: string; last_name?: string; phone?: string; whatsapp?: string }) =>
    api.put<UserProfile>('/api/auth/profile', data),
}

// HomeCards API
export const homeCardsApi = {
  listAdmin: () =>
    api.get<HomeCard[]>('/api/home-cards/admin'),
  create: (data: HomeCardCreate) =>
    api.post<HomeCard>('/api/home-cards/admin', data),
  update: (id: number, data: Partial<HomeCardCreate>) =>
    api.put<HomeCard>(`/api/home-cards/admin/${id}`, data),
  delete: (id: number) =>
    api.delete(`/api/home-cards/admin/${id}`),
  reorder: (items: { id: number; sort_order: number }[]) =>
    api.post('/api/home-cards/admin/reorder', items),
}

// Login History API
export const loginHistoryApi = {
  list: (params?: { page?: number; page_size?: number; user_id?: number }) =>
    api.get<LoginHistoryEntry[]>('/api/login-history', { params }),
}

// App Logs (Telemetry) API
export const appLogsApi = {
  list: (params?: {
    page?: number
    page_size?: number
    level?: string
    tag?: string
    platform?: string
    search?: string
  }) => api.get('/api/logs', { params }),

  clear: (older_than_days?: number) =>
    api.delete('/api/logs', { params: { older_than_days: older_than_days ?? 30 } }),
}

// Users API
export const usersApi = {
  list: (params?: { page?: number; page_size?: number; search?: string; is_active?: boolean; tenant_id?: number }) =>
    api.get<PaginatedResponse<User>>('/api/users', { params }),

  get: (id: number) =>
    api.get<User>(`/api/users/${id}`),

  create: (data: {
    email: string; first_name: string; last_name: string
    password: string; role_ids: number[]
    phone?: string; whatsapp?: string; mobile_access?: boolean
  }) =>
    api.post<User>('/api/users', data),

  update: (id: number, data: Partial<User> & { role_ids?: number[]; new_password?: string }) =>
    api.put<User>(`/api/users/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/users/${id}`),

  toggleActive: (id: number) =>
    api.post<User>(`/api/users/${id}/toggle-active`),

  resetPassword: (id: number, newPassword: string) =>
    api.post(`/api/users/${id}/reset-password`, { new_password: newPassword }),

  getTenantAccess: (userId: number) =>
    api.get<TenantAccess[]>(`/api/users/${userId}/tenant-access`),

  toggleTenantAccess: (userId: number, targetTenantId: number) =>
    api.post<TenantAccess>(`/api/users/${userId}/toggle-tenant-access/${targetTenantId}`),

  addToTenant: (userId: number, targetTenantId: number) =>
    api.post<TenantAccess>(`/api/users/${userId}/add-to-tenant/${targetTenantId}`),
}

// Roles API
export const rolesApi = {
  list: () =>
    api.get<import('@/types').Role[]>('/api/users/roles'),
  create: (data: { name: string; description?: string; permissions: string[] }) =>
    api.post<import('@/types').Role>('/api/users/roles', data),
  update: (id: number, data: { name?: string; description?: string; permissions?: string[] }) =>
    api.put<import('@/types').Role>(`/api/users/roles/${id}`, data),
  delete: (id: number) =>
    api.delete(`/api/users/roles/${id}`),
}

// Tenants API
export const tenantsApi = {
  list: (params?: { page?: number; page_size?: number }) => 
    api.get<PaginatedResponse<Tenant>>('/api/tenants', { params }),
  
  get: (id: number) => 
    api.get<Tenant>(`/api/tenants/${id}`),
  
  create: (data: Partial<Tenant>) => 
    api.post<Tenant>('/api/tenants', data),
  
  update: (id: number, data: Partial<Tenant>) => 
    api.put<Tenant>(`/api/tenants/${id}`, data),
  
  delete: (id: number) => 
    api.delete(`/api/tenants/${id}`),
}

// Devices API
export const devicesApi = {
  list: (params?: { page?: number; page_size?: number }) => 
    api.get<PaginatedResponse<Device>>('/api/devices', { params }),
  
  myDevices: () => 
    api.get<PaginatedResponse<Device>>('/api/devices/my'),
  
  get: (id: number) => 
    api.get<Device>(`/api/devices/${id}`),
  
  update: (id: number, data: Partial<Device>) => 
    api.put<Device>(`/api/devices/${id}`, data),
  
  delete: (id: number) => 
    api.delete(`/api/devices/${id}`),
  
  sync: (id: number) => 
    api.post(`/api/devices/${id}/sync`),
}

// Forms API
export const formsApi = {
  list: (params?: { page?: number; page_size?: number; search?: string; category?: string }) =>
    api.get<{ items: DynamicForm[]; total: number; page: number; page_size: number }>('/api/forms', { params }),

  get: (id: number) =>
    api.get<DynamicForm>(`/api/forms/${id}`),

  create: (data: Partial<DynamicForm>) =>
    api.post<DynamicForm>('/api/forms', data),

  update: (id: number, data: Partial<DynamicForm>) =>
    api.put<DynamicForm>(`/api/forms/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/forms/${id}`),

  toggle: (id: number) =>
    api.patch<DynamicForm>(`/api/forms/${id}/toggle`, {}),

  linkWorkflow: (id: number, workflowId: number | null) =>
    api.patch<DynamicForm>(`/api/forms/${id}/workflow`, { workflow_id: workflowId }),

  submissions: (formId: number, params?: { page?: number; page_size?: number; status?: string }) =>
    api.get<PaginatedResponse<Submission>>(`/api/forms/${formId}/submissions`, { params }),
}

// Submissions API (cross-form, admin)
export const submissionsApi = {
  listAll: (params?: { page?: number; page_size?: number; form_id?: number; status?: string }) =>
    api.get<PaginatedResponse<Submission>>('/api/submissions', { params }),

  get: (id: number) =>
    api.get<SubmissionDetail>(`/api/submissions/${id}`),

  delete: (id: number) =>
    api.delete(`/api/submissions/${id}`),
}

// Workflows API
export const workflowsApi = {
  // Definitions
  list: () =>
    api.get<WorkflowDefinition[]>('/api/workflows'),

  get: (id: number) =>
    api.get<WorkflowDefinitionDetail>(`/api/workflows/${id}`),

  create: (data: { name: string; description?: string; category?: string }) =>
    api.post<WorkflowDefinition>('/api/workflows', data),

  update: (id: number, data: { name?: string; description?: string; category?: string; is_active?: boolean; initial_state_id?: number }) =>
    api.put<WorkflowDefinition>(`/api/workflows/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/workflows/${id}`),

  // States
  addState: (workflowId: number, data: { name: string; description?: string; color?: string; is_initial?: boolean; is_final?: boolean; requires_approval?: boolean; sla_hours?: number; assigned_role_id?: number }) =>
    api.post<WorkflowState>(`/api/workflows/${workflowId}/states`, data),

  updateState: (workflowId: number, stateId: number, data: Partial<WorkflowState>) =>
    api.put<WorkflowState>(`/api/workflows/${workflowId}/states/${stateId}`, data),

  deleteState: (workflowId: number, stateId: number) =>
    api.delete(`/api/workflows/${workflowId}/states/${stateId}`),

  // Transitions
  addTransition: (workflowId: number, data: { from_state_id: number; to_state_id: number; action_name: string; required_role_id?: number; auto_transition?: boolean; label_template_id?: number | null; label_required?: boolean }) =>
    api.post<WorkflowTransition>(`/api/workflows/${workflowId}/transitions`, data),

  deleteTransition: (workflowId: number, transitionId: number) =>
    api.delete(`/api/workflows/${workflowId}/transitions/${transitionId}`),

  updateTransition: (workflowId: number, transitionId: number, data: { from_state_id: number; to_state_id: number; action_name: string; required_role_id?: number; auto_transition?: boolean; label_template_id?: number | null; label_required?: boolean }) =>
    api.put<WorkflowTransition>(`/api/workflows/${workflowId}/transitions/${transitionId}`, data),

  // Instances
  listInstances: (params?: { is_active?: boolean; workflow_id?: number }) =>
    api.get<WorkflowInstance[]>('/api/workflows/instances', { params }),

  createInstance: (data: { workflow_id: number; form_submission_id?: number; reference_id?: number; reference_type?: string }) =>
    api.post<WorkflowInstanceDetail>('/api/workflows/instances', data),

  getInstance: (id: number) =>
    api.get<WorkflowInstanceDetail>(`/api/workflows/instances/${id}`),

  advanceInstance: (id: number, data: { transition_id: number; comment?: string }) =>
    api.post<WorkflowInstanceDetail>(`/api/workflows/instances/${id}/advance`, data),

  // Tasks
  myTasks: () =>
    api.get<WorkflowTask[]>('/api/workflows/my-tasks'),

  completeTask: (taskId: number) =>
    api.post<WorkflowTask>(`/api/workflows/tasks/${taskId}/complete`),

  approveTask: (taskId: number, data: { transition_id: number; comment?: string }) =>
    api.post<WorkflowTask>(`/api/workflows/tasks/${taskId}/approve`, data),

  getInstanceHistory: (id: number) =>
    api.get<{
      id: number
      from_state_name?: string
      to_state_name: string
      to_state_color?: string
      action_name?: string
      performed_by_name?: string
      comment?: string
      created_at: string
    }[]>(`/api/workflows/instances/${id}/history`),
}

// Releases API
export const releasesApi = {
  list: () =>
    api.get<{ items: AppRelease[]; total: number }>('/api/admin/releases'),

  create: (data: { version: string; apk_url?: string; ios_url?: string; changelog?: string }) =>
    api.post<AppRelease>('/api/admin/releases', data),

  delete: (id: number) =>
    api.delete(`/api/admin/releases/${id}`),
}

// Clients API
export const clientesApi = {
  list: (params?: { search?: string; active_only?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.active_only) qs.set('active_only', 'true')
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ items: Client[]; total: number }>(`/api/admin/clients${query}`)
  },
  get: (id: number) => api.get<Client>(`/api/admin/clients/${id}`),
  create: (data: ClientCreate) => api.post<Client>('/api/admin/clients', data),
  update: (id: number, data: ClientUpdate) => api.put<Client>(`/api/admin/clients/${id}`, data),
  delete: (id: number) => api.delete(`/api/admin/clients/${id}`),
}

// Categories API
export const categoriesApi = {
  list: () =>
    api.get<{ id: number; name: string; created_at: string }[]>('/api/admin/categories'),

  create: (name: string) =>
    api.post<{ id: number; name: string; created_at: string }>('/api/admin/categories', { name }),

  delete: (id: number) =>
    api.delete(`/api/admin/categories/${id}`),

  rename: (id: number, name: string) =>
    api.patch<{ id: number; name: string; created_at: string }>(`/api/admin/categories/${id}`, { name }),
}

// Departments API
export const departmentsApi = {
  list: () =>
    api.get<{ id: number; name: string; code: string | null; is_active: boolean; created_at: string }[]>('/api/admin/departments'),

  create: (data: { name: string; code?: string }) =>
    api.post<{ id: number; name: string; code: string | null; is_active: boolean; created_at: string }>('/api/admin/departments', data),

  update: (id: number, data: { name?: string; code?: string; is_active?: boolean }) =>
    api.put<{ id: number; name: string; code: string | null; is_active: boolean; created_at: string }>(`/api/admin/departments/${id}`, data),

  delete: (id: number) =>
    api.delete(`/api/admin/departments/${id}`),
}

// Master Data (custom attribute lists) API
export const masterDataApi = {
  // Lists
  getLists: () =>
    api.get<{ id: number; slug: string; name: string; description: string | null; is_active: boolean; sort_order: number; item_count: number }[]>('/api/master-data/lists'),

  createList: (data: { name: string; description?: string; sort_order?: number }) =>
    api.post<{ id: number; slug: string; name: string; description: string | null; is_active: boolean; sort_order: number; item_count: number }>('/api/master-data/lists', data),

  updateList: (id: number, data: { name?: string; description?: string; is_active?: boolean; sort_order?: number }) =>
    api.put<{ id: number; slug: string; name: string; description: string | null; is_active: boolean; sort_order: number; item_count: number }>(`/api/master-data/lists/${id}`, data),

  deleteList: (id: number) =>
    api.delete(`/api/master-data/lists/${id}`),

  // Items
  getItems: (listId: number) =>
    api.get<{ id: number; value: string; label: string | null; sort_order: number; is_active: boolean }[]>(`/api/master-data/lists/${listId}/items`),

  createItem: (listId: number, data: { value: string; label?: string; sort_order?: number }) =>
    api.post<{ id: number; value: string; label: string | null; sort_order: number; is_active: boolean }>(`/api/master-data/lists/${listId}/items`, data),

  updateItem: (listId: number, itemId: number, data: { value?: string; label?: string; sort_order?: number; is_active?: boolean }) =>
    api.put<{ id: number; value: string; label: string | null; sort_order: number; is_active: boolean }>(`/api/master-data/lists/${listId}/items/${itemId}`, data),

  deleteItem: (listId: number, itemId: number) =>
    api.delete(`/api/master-data/lists/${listId}/items/${itemId}`),
}

// Reports API
export const reportsApi = {
  overview: () => api.get('/api/reports/overview'),
}

// Forms copy API
export const formsCopyApi = {
  getAccessibleTenants: () =>
    api.get<{ tenants: { slug: string; name: string; id: number }[] }>('/api/auth/my-tenants'),
  copy: (formId: number, targetTenantIds: number[]) =>
    api.post<{ results: { tenant_id: number; tenant_name: string; tenant_slug: string; form_id: number; status: string }[] }>(
      `/api/forms/${formId}/copy`, { target_tenant_ids: targetTenantIds }
    ),
}

// Settings / SMTP API
export const settingsApi = {
  getSmtp: () => api.get('/api/settings/smtp'),
  saveSmtp: (data: object) => api.put('/api/settings/smtp', data),
  testSmtp: (data: object) => api.post('/api/settings/smtp/test', data),
  getNotifications: () => api.get('/api/settings/notifications'),
  updateNotifications: (updates: { notification_type: string; is_active: boolean }[]) =>
    api.put('/api/settings/notifications', updates),
}

// Audit Log API
export const auditApi = {
  list: (params?: {
    page?: number
    page_size?: number
    action?: string
    entity_type?: string
    user_id?: number
    search?: string
    date_from?: string
    date_to?: string
  }) => api.get<import('@/types').AuditLogListResponse>('/api/audit/logs', { params }),

  getActions: () => api.get<string[]>('/api/audit/logs/actions'),
  getEntityTypes: () => api.get<string[]>('/api/audit/logs/entity-types'),
}
